// import React, { useMemo } from 'react';
// import { Card, CardHeader, Heading, CardBody, Box, Flex, useColorMode } from '@chakra-ui/react';
// import ReactApexChart from 'react-apexcharts';

// const GrowthRateApexChart = ({ data, height = 400, title = 'Sales Growth Rate' }) => {
//    const { colorMode } = useColorMode();
//    const categories = useMemo(() => data.map((d) => d.customer_name), [data]);
//    const numericHeight = typeof height === 'string' ? parseInt(height, 10) : height;
//    const chartHeight = numericHeight - 56;

//    const series = useMemo(
//       () => [
//          {
//             name: 'Previous Sales',
//             data: data.map((d) => d.previous_sales),
//          },
//          {
//             name: 'Current Sales',
//             data: data.map((d) => d.current_sales),
//          },
//       ],
//       [data],
//    );

//    const options = useMemo(
//       () => ({
//          chart: {
//             type: 'bar',
//             stacked: false,
//             toolbar: {
//                show: true,
//                tools: {
//                   download: true,
//                   selection: false,
//                   zoom: false,
//                   zoomin: false,
//                   zoomout: false,
//                   pan: false,
//                   reset: false,
//                },
//             },
//             zoom: { enabled: false },
//             background: 'transparent',
//          },
//          plotOptions: {
//             bar: {
//                horizontal: false,
//                columnWidth: '50%',
//                borderRadius: 4,
//             },
//          },
//          dataLabels: {
//             enabled: false,
//          },
//          stroke: {
//             show: true,
//             width: 2,
//             colors: ['transparent'],
//          },
//          xaxis: {
//             categories,
//             labels: {
//                rotate: -45,
//                style: {
//                   fontSize: '12px',
//                   colors: colorMode === 'dark' ? '#E2E8F0' : '#4A5568',
//                },
//             },
//             title: {
//                text: 'Customer',
//                style: {
//                   color: colorMode === 'dark' ? '#E2E8F0' : '#4A5568',
//                },
//             },
//             axisBorder: {
//                show: true,
//                color: colorMode === 'dark' ? '#4A5568' : '#E2E8F0',
//             },
//             axisTicks: {
//                show: true,
//                color: colorMode === 'dark' ? '#4A5568' : '#E2E8F0',
//             },
//          },
//          yaxis: {
//             title: {
//                text: 'Sales Amount (₹)',
//                style: {
//                   color: colorMode === 'dark' ? '#E2E8F0' : '#4A5568',
//                },
//             },
//             labels: {
//                style: {
//                   colors: colorMode === 'dark' ? '#E2E8F0' : '#4A5568',
//                },
//                formatter: (val) => `₹${val.toLocaleString('en-IN')}`,
//             },
//          },
//          tooltip: {
//             theme: colorMode,
//             shared: true,
//             intersect: false,
//             y: {
//                formatter: (val, { seriesIndex, dataPointIndex }) => {
//                   const record = data[dataPointIndex];
//                   const amount = val.toLocaleString('en-IN');
//                   const growth = record?.growth_percentage || '0.00';
//                   return `₹${amount} (Growth: ${growth}%)`;
//                },
//             },
//          },
//          colors: colorMode === 'dark' ? ['#A0AEC0', '#4299E1'] : ['#718096', '#3182CE'], // gray.500/blue.500 for dark, gray.600/blue.600 for light
//          legend: {
//             position: 'top',
//             labels: {
//                colors: colorMode === 'dark' ? '#E2E8F0' : '#4A5568',
//             },
//          },
//          grid: {
//             borderColor: colorMode === 'dark' ? '#4A5568' : '#E2E8F0',
//          },
//       }),
//       [categories, colorMode, data],
//    );

//    return (
//       <Card bg={colorMode === 'dark' ? 'gray.700' : 'white'}>
//          <CardHeader>
//             <Heading size="md" color={colorMode === 'dark' ? 'white' : 'gray.800'}>
//                {title}
//             </Heading>
//          </CardHeader>
//          <CardBody p={0}>
//             <Box>
//                <ReactApexChart options={options} series={series} type="bar" height={chartHeight} />
//             </Box>
//          </CardBody>
//       </Card>
//    );
// };

// export default GrowthRateApexChart;

import React, { useMemo, useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { RotateCw } from 'lucide-react';
import { getGrowthRate } from '../../../../redux/actions/dashboard';
import { useDispatch } from 'react-redux';

const GrowthRateApexChart = ({ data, height = 400, title = 'Sales Growth Rate', financialAnalysisDateRange, user }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   const categories = useMemo(() => data.map((d) => d.customer_name), [data]);
   const numericHeight = typeof height === 'string' ? parseInt(height, 10) : height;
   const chartHeight = numericHeight - 56;

   const series = useMemo(
      () => [
         {
            name: 'Previous Sales',
            data: data.map((d) => d.previous_sales),
            shadow: {
               enabled: true,
               color: isDark ? '#000000' : '#718096',
               top: 3,
               left: 2,
               blur: 4,
               opacity: 0.1,
            },
         },
         {
            name: 'Current Sales',
            data: data.map((d) => d.current_sales),
            shadow: {
               enabled: true,
               color: isDark ? '#000000' : '#3182CE',
               top: 3,
               left: 2,
               blur: 6,
               opacity: 0.2,
            },
         },
      ],
      [data, isDark],
   );

   const options = useMemo(
      () => ({
         chart: {
            type: 'line',
            stacked: false,
            toolbar: {
               show: true,
               tools: {
                  download: true,
                  selection: false,
                  zoom: false,
                  zoomin: false,
                  zoomout: false,
                  pan: false,
                  reset: false,
               },
            },
            zoom: { enabled: false },
            background: 'transparent',
         },
         stroke: {
            width: 3,
            curve: 'smooth',
         },
         markers: {
            size: 5,
            hover: {
               size: 7,
            },
         },
         dataLabels: {
            enabled: false,
         },
         xaxis: {
            categories,
            labels: {
               rotate: -45,
               style: {
                  fontSize: '12px',
                  colors: isDark ? '#E2E8F0' : '#4A5568',
               },
            },
            title: {
               text: 'Customer',
               style: {
                  color: isDark ? '#E2E8F0' : '#4A5568',
               },
            },
            axisBorder: {
               show: true,
               color: isDark ? '#4A5568' : '#E2E8F0',
            },
            axisTicks: {
               show: true,
               color: isDark ? '#4A5568' : '#E2E8F0',
            },
         },
         yaxis: {
            title: {
               text: 'Sales Amount (₹)',
               style: {
                  color: isDark ? '#E2E8F0' : '#4A5568',
               },
            },
            labels: {
               style: {
                  colors: isDark ? '#E2E8F0' : '#4A5568',
               },
               formatter: (val) => `₹${val.toLocaleString('en-IN')}`,
            },
         },
         tooltip: {
            theme: colorMode,
            shared: true,
            intersect: false,
            y: {
               formatter: (val, { seriesIndex, dataPointIndex }) => {
                  const record = data[dataPointIndex];
                  const amount = val.toLocaleString('en-IN');
                  const growth = record?.growth_percentage || '0.00';
                  return `₹${amount} (Growth: ${growth}%)`;
               },
            },
         },
         colors: isDark ? ['#A0AEC0', '#4299E1'] : ['#718096', '#3182CE'],
         legend: {
            position: 'top',
            labels: {
               colors: isDark ? '#E2E8F0' : '#4A5568',
            },
         },
         grid: {
            borderColor: isDark ? '#4A5568' : '#E2E8F0',
         },
      }),
      [categories, colorMode, isDark, data],
   );

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getGrowthRate({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <div className={`rounded-lg shadow-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
         <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center gap-3">
            <div className={`w-1 h-8 rounded-full ${isDark ? 'bg-blue-500' : 'bg-blue-600'}`}></div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h2>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${isDark ? "hover:bg-gray-600 text-blue-400" : "hover:bg-gray-100 text-blue-600"
                  }`}
            >
               <RotateCw
                  className={`w-5 h-5 transform ${isRotating ? "rotate-[360deg]" : ""
                     } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? "700ms" : "10ms",
                  }}
               />
            </button>
         </div>
         <div className="p-0">
            <div>
               <ReactApexChart options={options} series={series} type="line" height={chartHeight} />
            </div>
         </div>
      </div>
   );
};

export default GrowthRateApexChart;
